import { isTravelTypeWithHousing } from '@repo/utils'
import React from 'react'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { useTravelTypeContext } from '../../contexts'
import { useCurrencyAreaContext } from '../../contexts/currency-area-context/useCurrencyAreaContext'
import { DownloadPdfDropdown } from '../../organisms/download-pdf-dropdown/DownloadPdfDropdown'
import UserCanAskUserAdminApprovalModal from '../../organisms/proposal/proposal-cta/user-current-action/user-can-ask-user-admin-approval/user-can-ask-user-admin-approval-modal/UserCanAskUserAdminApprovalModal'
import UserCanConfirmBookingModal from '../../organisms/proposal/proposal-cta/user-current-action/user-can-confirm-booking/user-can-confirm-booking-modal/UserCanConfirmBookingModal'
import { Button } from '../../../shadcn/components/ui/button'
import { decimalPrice } from '../../../utils'

export type SuggestionConfirmBookingFooterActionMode =
  | {
      confirmAction: 'can-confirm'
      callback: (arg: { clientProposalId: string }) => void
      isLoading: boolean
    }
  | {
      confirmAction: 'can-ask-admin-approval'
      callback: (arg: { clientProposalId: string; comment?: string }) => void
      isLoading: boolean
    }
  | {
      confirmAction: 'none'
    }

type SuggestionConfirmBookingFooterProps = {
  clientProposalId: string
  clientRequestId: string
  priceWithoutVat?: number
  priceWithVat?: number
  houseId?: string
} & SuggestionConfirmBookingFooterActionMode

export const SuggestionConfirmBookingFooter = (
  props: SuggestionConfirmBookingFooterProps,
) => {
  const {
    clientProposalId,
    clientRequestId,
    priceWithVat,
    priceWithoutVat,
    houseId,
  } = props
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const { currency } = useCurrencyAreaContext()

  const totalHT = decimalPrice(priceWithoutVat, { currency })
  const totalTTC = decimalPrice(priceWithVat, { currency })

  const total = (
    <div className='flex h-full min-h-10 w-full flex-row items-center justify-end gap-4 rounded-md border border-black bg-grey-300 px-3 text-xs font-medium'>
      <div className='mr-auto'>
        <FormattedMessage
          id='ui.totalPrice.total.label'
          defaultMessage='Total'
        />
      </div>
      <div>
        <FormattedMessage
          id='ui.totalPrice.HT'
          defaultMessage='{totalHT} HT'
          values={{ totalHT }}
        />
      </div>
      <div>
        <FormattedMessage
          id='ui.totalPrice.TTC'
          defaultMessage='{totalTTC} TTC'
          values={{ totalTTC }}
        />
      </div>
    </div>
  )

  const { travelType } = useTravelTypeContext()

  const downloadPdfDropdown = isTravelTypeWithHousing(travelType) ? (
    <DownloadPdfDropdown
      triggerClassName='w-full'
      clientProposalId={clientProposalId}
      clientRequestId={clientRequestId}
      houseId={houseId}
    />
  ) : null

  const getContent = () => {
    if (props.confirmAction === 'none') {
      return (
        <div className='flex w-full flex-row gap-2'>
          <div>{downloadPdfDropdown}</div>
          <div className='flex-grow'>{total}</div>
        </div>
      )
    }

    const { callback, isLoading } = props

    const getConfirmationModal = () => {
      switch (props.confirmAction) {
        case 'can-confirm':
          return (
            <UserCanConfirmBookingModal
              clientRequestId={clientRequestId}
              selectedProposalId={clientProposalId}
              isOpen={isConfirmationOpen}
              closeModal={() => setIsConfirmationOpen(false)}
              onSubmit={() => callback({ clientProposalId })}
              isLoadingSubmit={isLoading}
            />
          )
        case 'can-ask-admin-approval':
          return (
            <UserCanAskUserAdminApprovalModal
              isOpen={isConfirmationOpen}
              closeModal={() => setIsConfirmationOpen(false)}
              isLoading={isLoading}
              onSubmit={({ comment }) =>
                callback({ clientProposalId, comment })
              }
            />
          )
      }
    }

    return (
      <div className='flex w-full flex-col gap-2'>
        <div className='w-full'>{total}</div>
        <div className='flex flex-row justify-stretch gap-2'>
          {downloadPdfDropdown}
          <Button
            className='w-full'
            onClick={() => setIsConfirmationOpen(true)}
            dataTestId='book-trip-button'
          >
            <FormattedMessage
              id='ui.SuggestionConfirmBookingFooter.bookTrip'
              defaultMessage='Réserver'
            />
          </Button>
          {getConfirmationModal()}
        </div>
      </div>
    )
  }

  return <div className='w-full p-4'>{getContent()}</div>
}
